import React, { useContext, useState } from 'react'

import styled from 'styled-components'
import Grid from '@mui/material/Grid'
import { Typography } from '@mui/material'
import { Box } from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import SoftBox from 'components/SoftBox'
import SoftTypography from 'components/SoftTypography'
import FormField from 'components/FormField'
import SoftButton from 'components/SoftButton'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import {
  createAssetForUser,
  createLiabilityForUser,
} from 'admin/utils/mutations'
import { IntakeContext } from 'context/IntakeContext'
import Slide from '@mui/material/Slide'
import { toast } from 'react-toastify'
import { useFinances } from 'hooks/useFinances'
import Checkbox from '@mui/material/Checkbox'
import { selectData } from 'layouts/onboarding'
import { FormControlLabel, FormGroup } from '@mui/material'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

const SelectInput = styled.select`
  display: flex;
  width: 100%;
  height: 2.5rem;
  font-size: 0.875rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 0.0625rem solid #d2d6da;
  color: #344767;
  letter-spacing: 0.00938em;
  font-family: Roboto;
  cursor: pointer;
  text-transform: capitalize;
  margin-top: 3px;
  &:active,
  &:focus,
  &:focus-visible {
    border-color: #17c1e8;
    box-shadow: 0 0 0 0.125rem rgba(129, 227, 249);
  }
`

export const BalanceSheetModalForm = ({
  canEdit,
  handleClose,
  type,
  subType,
  name,
  amount,
  setSubType,
  setType,
  setName,
  setAmount,
  open,
  subTypeClassification,
  setSubTypeClassification,
  interestRate,
  setInterestRate,
  handleSubmit,
  typeSubTypeUuid,
  setKeepInRetirement,
  keepInRetirement,
  setMonthlyPayment,
  monthlyPayment,
  frequency,
  setFrequency,
  loanEndDate,
  setLoanEndDate,
  reloadAccounts,
  subTypeOptions = [],
  hideName,
  plaidName,
  modalHeader,
  newStyle = false,
}) => {
  const { createOrUpdateAccount, deleteAccount } = useFinances()
  const checked = keepInRetirement || false

  const onSubmit = async () => {
    await createOrUpdateAccount({
      name,
      amount,
      type,
      subType,
      subTypeClassification,
      interestRate,
      typeSubTypeUuid,
      keepInRetirement,
      loanEndDate,
      monthlyPayment,
      frequency,
    })
    toast.info(
      `You have successfully ${typeSubTypeUuid ? 'updated' : 'created'} ${
        type === 'liability' ? 'a' : 'an'
      } ${type}`,
      {
        position: 'top-center',
        autoClose: 5000,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
      }
    )
    reloadAccounts()
    handleSubmit()
  }

  const onDelete = async () => {
    if (window.confirm('Are you sure?')) {
      await deleteAccount({ typeSubTypeUuid })
      toast.info(`You have successfully deleted account "${name}"`)
      reloadAccounts()
    }
    handleSubmit()
  }

  return (
    <>
      {!newStyle ? (
        <Dialog
          open={open}
          TransitionComponent={Transition}
          keepMounted
          onClose={handleClose}
          aria-describedby="alert-dialog-slide-description"
          key={open ? 'open' : 'closed'}
        >
          <DialogTitle>
            {modalHeader}{' '}
            {plaidName ? (
              <>
                <br />
                <span style={{ fontSize: '.6em', verticalAlign: 'center' }}>
                  Synced from Plaid as: {plaidName}
                </span>
              </>
            ) : null}
          </DialogTitle>
          <DialogContent>
            <Grid item xs={12} data-testid="balanceSheetModal">
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6} sx={{ display: 'none' }}>
                  <SoftTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Select Type
                  </SoftTypography>
                  <SelectInput
                    name="type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                  >
                    <option value="">Select Type</option>
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                  </SelectInput>
                </Grid>
                {/* Asset/Liability or Deduction */}
                {subTypeOptions.length ? (
                  <Grid item xs={12} sm={6}>
                    <SoftTypography
                      component="label"
                      variant="caption"
                      fontWeight="normal"
                      textTransform="capitalize"
                      sx={{ lineHeight: 1.6, fontSize: '1rem' }}
                    >
                      Select Type
                    </SoftTypography>
                    <SelectInput
                      name="subType"
                      value={subType}
                      onChange={(e) => setSubType(e.target.value)}
                    >
                      <option value="">Select Type</option>
                      {subTypeOptions.map(({ value, label }) => {
                        return (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        )
                      })}
                    </SelectInput>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      {renderMuiFormField({
                        autoFocus: false,
                        label: 'Select Category',
                        placeholder: '',
                        inputType: 'select',
                        value: subType,
                        onChange: (e) => setSubType(e),
                        options: selectData.balanceSheetSubType,
                        optionMatchMethod: type,
                      })}
                    </Grid>
                    {type !== 'income' && type !== 'expense' && (
                      <Grid item xs={12} sm={6}>
                        {renderMuiFormField({
                          autoFocus: false,
                          label: 'Select Type',
                          placeholder: '',
                          inputType: 'select',
                          value: subTypeClassification,
                          onChange: (e) => setSubTypeClassification(e),
                          options: selectData.balanceSheetSubTypeClassification,
                          optionMatchMethod: subType,
                        })}
                      </Grid>
                    )}
                  </>
                )}
                {hideName ? null : (
                  <Grid item xs={12} sm={6}>
                    <SoftBox
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      {renderMuiFormField({
                        autoFocus: false,
                        label: 'Name',
                        placeholder: '',
                        inputType: 'text',
                        value: name,
                        onChange: (e) => setName(e),
                      })}
                    </SoftBox>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <SoftBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                    sx={{ '>div': { marginBottom: '3px' } }}
                  >
                    {renderMuiFormField({
                      inputType: 'currencyFloat',
                      placeholder: '$XX,XXX.XX',
                      label:
                        type === 'liability' ? 'Remaining Balance' : 'Amount',
                      value: amount,
                      onChange: setAmount,
                    })}
                  </SoftBox>
                </Grid>
                {type === 'liability' ? (
                  <Grid item xs={12} sm={6}>
                    <SoftBox
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      {renderMuiFormField({
                        inputType: 'percentage',
                        placeholder: 'Ex. 15.0%',
                        label: 'Interest Rate',
                        value: interestRate,
                        onChange: setInterestRate,
                      })}
                    </SoftBox>
                  </Grid>
                ) : null}
                {type === 'liability' ? (
                  <Grid item xs={12} sm={6}>
                    <SoftBox
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      {renderMuiFormField({
                        inputType: 'currencyFloat',
                        placeholder: '$XX,XXX.XX',
                        label:
                          subType === 'tspLoan'
                            ? 'Bi-weekly Payment'
                            : 'Monthly Payment',
                        value: monthlyPayment,
                        onChange: setMonthlyPayment,
                      })}
                    </SoftBox>
                  </Grid>
                ) : null}
                {type === 'liability' ? (
                  <Grid item xs={12} sm={6}>
                    <SoftBox
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      {renderMuiFormField({
                        inputType: 'date',
                        placeholder: 'MM/DD/YYYY',
                        label: 'Loan End Date',
                        value: loanEndDate,
                        onChange: setLoanEndDate,
                      })}
                    </SoftBox>
                  </Grid>
                ) : null}
                {type === 'income' || type === 'expense' ? (
                  <Grid item xs={12} sm={6}>
                    <SoftBox
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      {renderMuiFormField({
                        inputType: 'select',
                        placeholder: 'Monthly / Annual',
                        label: 'Frequency',
                        value: frequency,
                        onChange: setFrequency,
                        options: [
                          { value: 'monthly', label: 'Monthly' },
                          { value: 'annual', label: 'Annual' },
                        ],
                      })}
                    </SoftBox>
                  </Grid>
                ) : null}
              </Grid>
              {setKeepInRetirement &&
              subType !== 'charity' &&
              subType !== 'fsa' &&
              subType !== 'hsa' &&
              subType !== 'alotsavings' &&
              subType !== 'alotchecking' &&
              subType !== 'alotother' ? (
                <Grid>
                  <SoftBox
                    display="flex"
                    flexDirection="row"
                    justifyContent="left"
                    height="100%"
                    style={{ margin: '10px' }}
                  >
                    <FormControlLabel
                      label="Keep in retirement?"
                      sx={{
                        '> span': {
                          fontWeight: 'normal !important',
                          fontSize: '1rem',
                        },
                      }}
                      control={
                        <Checkbox
                          name="keepInRetirement"
                          onChange={(e) =>
                            setKeepInRetirement(e.target.checked)
                          }
                          checked={checked}
                        />
                      }
                    ></FormControlLabel>
                  </SoftBox>
                </Grid>
              ) : null}
            </Grid>
          </DialogContent>
          <DialogActions>
            <SoftButton
              variant="text"
              color="error"
              onClick={onDelete}
              data-testid="balanceSheetModalDeleteButton"
            >
              Delete
            </SoftButton>
            <SoftButton
              variant="text"
              color="dark"
              onClick={handleClose}
              data-testid="balanceSheetModalCancelButton"
            >
              Cancel
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="dark"
              sx={{ height: '100%' }}
              onClick={onSubmit}
              data-testid="balanceSheetModalSaveButton"
            >
              Save
            </SoftButton>
          </DialogActions>
        </Dialog>
      ) : (
        <Grid item xs={12} data-testid="balanceSheetModal">
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} sm={6} sx={{ display: 'none' }}>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
                textTransform="capitalize"
              >
                Select Type
              </SoftTypography>
              <SelectInput
                name="type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="">Select Type</option>
                <option value="asset">Asset</option>
                <option value="liability">Liability</option>
              </SelectInput>
            </Grid>
            {/* Asset/Liability or Deduction */}
            {subTypeOptions.length ? (
              <Grid item xs={12} sm={6}>
                <SoftTypography
                  component="label"
                  variant="caption"
                  fontWeight="normal"
                  textTransform="capitalize"
                  sx={{ lineHeight: 1.6, fontSize: '1rem' }}
                >
                  Select Type
                </SoftTypography>
                <SelectInput
                  name="subType"
                  value={subType}
                  onChange={(e) => setSubType(e.target.value)}
                >
                  <option value="">Select Type</option>
                  {subTypeOptions.map(({ value, label }) => {
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    )
                  })}
                </SelectInput>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    label: 'Select Category',
                    placeholder: '',
                    inputType: 'select',
                    value: subType,
                    onChange: (e) => setSubType(e),
                    options: selectData.balanceSheetSubType,
                    optionMatchMethod: type,
                  })}
                </Grid>
                {type !== 'income' && type !== 'expense' && (
                  <Grid item xs={12} sm={6}>
                    {renderMuiFormField({
                      autoFocus: false,
                      label: 'Select Type',
                      placeholder: '',
                      inputType: 'select',
                      value: subTypeClassification,
                      onChange: (e) => setSubTypeClassification(e),
                      options: selectData.balanceSheetSubTypeClassification,
                      optionMatchMethod: subType,
                    })}
                  </Grid>
                )}
              </>
            )}
            {hideName ? null : (
              <Grid item xs={12} sm={6}>
                <SoftBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <FormField
                    type="text"
                    label="Name"
                    name="name"
                    value={name}
                    placeholder=""
                    onChange={(e) => setName(e.target.value)}
                  />
                </SoftBox>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <SoftBox
                display="flex"
                flexDirection="column"
                justifyContent="flex-end"
                height="100%"
                sx={{ '>div': { marginBottom: '3px' } }}
              >
                {renderMuiFormField({
                  inputType: 'currencyFloat',
                  placeholder: '$XX,XXX.XX',
                  label: type === 'liability' ? 'Remaining Balance' : 'Amount',
                  value: amount,
                  onChange: setAmount,
                })}
              </SoftBox>
            </Grid>
            {type === 'liability' ? (
              <Grid item xs={12} sm={6}>
                <SoftBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  {renderMuiFormField({
                    inputType: 'percentage',
                    placeholder: 'Ex. 15.0%',
                    label: 'Interest Rate',
                    value: interestRate,
                    onChange: setInterestRate,
                  })}
                </SoftBox>
              </Grid>
            ) : null}
            {type === 'liability' ? (
              <Grid item xs={12} sm={6}>
                <SoftBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  {renderMuiFormField({
                    inputType: 'currencyFloat',
                    placeholder: '$XX,XXX.XX',
                    label:
                      subType === 'tspLoan'
                        ? 'Bi-weekly Payment'
                        : 'Monthly Payment',
                    value: monthlyPayment,
                    onChange: setMonthlyPayment,
                  })}
                </SoftBox>
              </Grid>
            ) : null}
            {type === 'liability' ? (
              <Grid item xs={12} sm={6}>
                <SoftBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  {renderMuiFormField({
                    inputType: 'date',
                    placeholder: 'MM/DD/YYYY',
                    label: 'Loan End Date',
                    value: loanEndDate,
                    onChange: setLoanEndDate,
                  })}
                </SoftBox>
              </Grid>
            ) : null}
            {type === 'income' || type === 'expense' ? (
              <Grid item xs={12} sm={6}>
                <SoftBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  {renderMuiFormField({
                    inputType: 'select',
                    placeholder: 'Monthly / Annual',
                    label: 'Frequency',
                    value: frequency,
                    onChange: setFrequency,
                    options: [
                      { value: 'monthly', label: 'Monthly' },
                      { value: 'annual', label: 'Annual' },
                    ],
                  })}
                </SoftBox>
              </Grid>
            ) : null}
          </Grid>
          {setKeepInRetirement &&
          subType !== 'charity' &&
          subType !== 'fsa' &&
          subType !== 'hsa' &&
          subType !== 'alotsavings' &&
          subType !== 'alotchecking' &&
          subType !== 'alotother' ? (
            <Grid>
              <SoftBox
                display="flex"
                flexDirection="row"
                justifyContent="left"
                height="100%"
                style={{ margin: '10px' }}
              >
                <FormControlLabel
                  label="Keep in retirement?"
                  sx={{
                    '> span': {
                      fontWeight: 'normal !important',
                      fontSize: '1rem',
                    },
                  }}
                  control={
                    <Checkbox
                      name="keepInRetirement"
                      onChange={(e) => setKeepInRetirement(e.target.checked)}
                      checked={checked}
                    />
                  }
                ></FormControlLabel>
              </SoftBox>
            </Grid>
          ) : null}

          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'space-between'}
            mt={5}
          >
            <Box>
              <Button
                variant="contained"
                onClick={handleClose}
                // disabled={}
                paddingX={3}
                paddingY={1}
                sx={{
                  backgroundColor: '#ef2370',
                  boxShadow: 'none',
                  borderRadius: '100px',
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#1c59bd',
                    boxShadow: 'none',
                  },
                }}
              >
                <Typography
                  variant="body1Medium"
                  textTransform={'capitalize'}
                  color={'#fff'}
                >
                  Cancel
                </Typography>
              </Button>
            </Box>
            <Box>
              {canEdit && (
                <Button
                  variant="contained"
                  onClick={onDelete}
                  // disabled={}
                  sx={{
                    padding: '8px 24px',
                    backgroundColor: '#000',
                    boxShadow: 'none',
                    borderRadius: '100px',
                    '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                      transform: 'none',
                      backgroundColor: '#1c59bd',
                      boxShadow: 'none',
                    },
                  }}
                >
                  <Typography
                    variant="body1Medium"
                    textTransform={'capitalize'}
                    color={'#fff'}
                  >
                    Delete
                  </Typography>
                </Button>
              )}
              <Button
                variant="contained"
                onClick={onSubmit}
                // disabled={}
                sx={{
                  padding: '8px 24px',
                  backgroundColor: '#2370ED',
                  boxShadow: 'none',
                  borderRadius: '100px',
                  '&:hover, &:focus, &:active, &:focus:not(:hover)': {
                    transform: 'none',
                    backgroundColor: '#1c59bd',
                    boxShadow: 'none',
                  },
                }}
              >
                <Typography
                  variant="body1Medium"
                  textTransform={'capitalize'}
                  color={'#fff'}
                >
                  Save
                </Typography>
              </Button>
            </Box>
          </Box>
        </Grid>
      )}
    </>
  )
}
